import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Loading from 'components/Loading';
import InscriptionsGrid from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionsGrid';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import { ColDef, GridApi, IRowNode } from 'ag-grid-community';
import AddInscription from 'routes/BudgetModule/components/shared/InscriptionsGrid/AddInscription/AddInscription';
import { getResourceNameField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { FEATURE_ORPHAN_INSCRIPTION, isEnabled } from 'helpers/features';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { getIsEditablePhase } from 'routes/BudgetModule/shared/phases/helpers';
import { styled } from '@mui/material/styles';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import {
    getAddInscriptionStepTitles,
    getExpenseOrIncomeFromFilters,
    getInscriptionsModalColumns,
    getInscriptionsQueryFilters,
} from './helpers';
import { useGetAnalyseInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import { useGetResourcesInBudgetProposal } from './hooks';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';

type Props = {
    onClose: (event: object, reason: 'backdropClick' | 'escapeKeyDown') => void;
    onOpen: (arg1: void) => void;
    open: boolean;
    baseFilters: QueryFilterT[];
    colDef: ColDef<unknown>;
    gridApi: GridApi;
    node: IRowNode;
    budgetProposal: BudgetProposalT;
};

const StyModal = styled(Box, {
    name: 'StyModal',
})({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    height: '75%',
    backgroundColor: 'white',
    // This style comes from the Dialog component to uniformize the style in this application
    // Dialog is not used directly because it doesn't match the use of this modal
    borderRadius: '4px',
    boxShadow:
        '0px 11px 15px -7px rgb(0 0 0 / 20%), 0px 24px 38px 3px rgb(0 0 0 / 14%), 0px 9px 46px 8px rgb(0 0 0 / 12%)',
});

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    padding: '2.5rem 2.5rem 3.5rem 2.5rem',
    width: '100%',
    height: '100%',
});

export default function InscriptionsModal({
    open,
    onOpen,
    onClose,
    baseFilters,
    colDef,
    gridApi,
    node,
    budgetProposal,
}: Props): React.ReactElement {
    useEffect(() => onOpen(), []);
    const { t } = useTranslation();
    const imputationRawDimensions = React.useContext(
        ImputationDimensionsContext,
    );

    const { projects } = useGetProjects();

    const { data: budgetProposals, isLoading: areBudgetProposalsLoading } =
        useGetBudgetProposals();

    const inscriptionsFilters = getInscriptionsQueryFilters(
        baseFilters,
        colDef,
        node,
        gridApi,
    );
    const expenseOrIncome = getExpenseOrIncomeFromFilters(inscriptionsFilters);

    const {
        data: inscriptionsData,
        isLoading: inscriptionsLoading,
        isError: fetchInscriptionsError,
        cacheKey: queryCacheKey,
    } = useGetAnalyseInscriptions({
        expenseOrIncome,
        additionalFilters: inscriptionsFilters,
        budgetBy: budgetProposal.by,
        phase: budgetProposal.phase,
    });

    // (explo-AMP Shortcut ID : 28345) momentary trick to rebuild an "inscriptionsWithMetadata"
    // sadly necessary because in some occasions, we need inscription.budgetProposalPhase
    const inscriptionsWithMetadata = useMemo(() => {
        if (!budgetProposals) return [];
        return inscriptionsData?.data.map((inscription: InscriptionT) => ({
            inscription,
        }));
    }, [budgetProposals, inscriptionsData]);

    const columns = useMemo(() => {
        if (!budgetProposals) return [];
        return getInscriptionsModalColumns(
            t,
            budgetProposal,
            budgetProposals,
            imputationRawDimensions,
            projects,
        );
    }, [budgetProposals]);

    const onCloseModal = (
        isInscriptionOrphan: boolean,
        selectedBudgetProposal: BudgetProposalT,
        selectedResourceName: string,
    ) => {
        if (isInscriptionOrphan) {
            trackEventBudget(
                TRACKABLE_EVENTS.budget.general.addOrphanInscriptionToggle,
                {
                    budgetProposalName: selectedBudgetProposal.name,
                    phase: selectedBudgetProposal.phase,
                    resourceName: selectedResourceName,
                },
            );
        }
    };
    const {
        data: budgetProposalResources = [],
        isLoading: areBudgetProposalResourcesLoading,
    } = useGetResourcesInBudgetProposal(
        budgetProposal.id,
        budgetProposal.by,
        inscriptionsFilters,
    );

    const addInscription = useMemo(() => {
        const resourceNameField = getResourceNameField(budgetProposal.by);
        return (
            <AddInscription
                stepTitles={getAddInscriptionStepTitles(t, resourceNameField)}
                budgetProposalResources={budgetProposalResources}
                resourceType={resourceNameField}
                expenseOrIncome={expenseOrIncome}
                showOperationColumn
                allowOrphanInscription={isEnabled(FEATURE_ORPHAN_INSCRIPTION)}
                onCloseModal={onCloseModal}
                inscriptionQueryCacheKey={queryCacheKey}
                isLoading={areBudgetProposalResourcesLoading}
            />
        );
    }, [
        expenseOrIncome,
        budgetProposal.by,
        t,
        queryCacheKey,
        budgetProposalResources,
        areBudgetProposalResourcesLoading,
    ]);

    const isLoading = inscriptionsLoading || areBudgetProposalsLoading;

    return (
        <Modal open={open} onClose={onClose}>
            <StyModal>
                {!isLoading && !fetchInscriptionsError && (
                    <StyGrid>
                        <InscriptionsGrid
                            // TODO StrictNullCheck : Try to remove "!"
                            budgetProposals={budgetProposals!}
                            expenseOrIncome={expenseOrIncome}
                            inscriptions={inscriptionsWithMetadata!}
                            // As the original Inscription is a custom column that we use specifically here
                            // we have to set its position in the grid.
                            // TODO: Use a defined order
                            columns={columns}
                            customGridSize="100%"
                            nbDisplayedInscriptions={8.5}
                            allowRowGroupStatusUpdate={getIsEditablePhase(
                                budgetProposal.phase,
                            )}
                            pagination
                            paginationPageSize={10}
                            allowFloatingFilters
                            queryCacheKey={queryCacheKey}
                        />
                        {addInscription}
                    </StyGrid>
                )}
                {isLoading && <Loading />}
                {fetchInscriptionsError && <p>{fetchInscriptionsError}</p>}
            </StyModal>
        </Modal>
    );
}
